/**
 * Athlete-level Reports section.
 *
 * Provides two one-click PDF exports:
 *   1. "Athlete Report"           → POST /api/premium-reports/athlete
 *   2. "Combined Athlete Report"  → POST /api/premium-reports/combined-athlete
 *
 * Both endpoints return a PDF blob; we trigger an anchor-click download.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { FileDown, Loader2, FileText, Layers, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const AthleteReports = ({ athleteId, athleteName }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, it) => (language === 'IT' ? it : en);

  const [busyKind, setBusyKind] = useState(null); // 'athlete' | 'combined'

  const downloadPdf = async (kind) => {
    const endpoint =
      kind === 'combined'
        ? '/premium-reports/combined-athlete'
        : '/premium-reports/athlete';
    const filename =
      kind === 'combined'
        ? `combined_athlete_${athleteId.slice(0, 8)}.pdf`
        : `athlete_report_${athleteId.slice(0, 8)}.pdf`;
    setBusyKind(kind);
    try {
      const res = await api.post(
        endpoint,
        { athlete_id: athleteId, language },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('Report downloaded', 'Report scaricato'));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          t('Export failed', 'Esportazione non riuscita'),
      );
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <Card
      className="bg-zinc-900 border-zinc-800 p-5"
      data-testid="athlete-reports-section"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
            <FileDown className="w-4 h-4 text-cyan-400" />
            {t('Reports', 'Report')}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {athleteName
              ? t(
                  `Premium PDF exports for ${athleteName}`,
                  `Export PDF premium per ${athleteName}`,
                )
              : t('Premium PDF exports', 'Export PDF premium')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/staff/roster/${athleteId}/season`)}
          className="border-zinc-700 text-zinc-100 hover:bg-zinc-800 justify-start h-auto py-3"
          data-testid="season-analytics-btn"
        >
          <div className="flex items-center gap-3 w-full">
            <BarChart3 className="w-5 h-5 text-cyan-400 shrink-0" />
            <div className="text-left">
              <div className="text-sm font-medium">
                {t('Season Analytics', 'Analytics di Stagione')}
              </div>
              <div className="text-xs text-zinc-500">
                {t('Interactive dashboard', 'Dashboard interattiva')}
              </div>
            </div>
          </div>
        </Button>

        <Button
          variant="outline"
          onClick={() => downloadPdf('athlete')}
          disabled={!!busyKind}
          className="border-zinc-700 text-zinc-100 hover:bg-zinc-800 justify-start h-auto py-3"
          data-testid="athlete-report-export-btn"
        >
          <div className="flex items-center gap-3 w-full">
            {busyKind === 'athlete' ? (
              <Loader2 className="w-5 h-5 animate-spin text-cyan-400 shrink-0" />
            ) : (
              <FileText className="w-5 h-5 text-cyan-400 shrink-0" />
            )}
            <div className="text-left">
              <div className="text-sm font-medium">
                {t('Athlete Report', 'Report Atleta')}
              </div>
              <div className="text-xs text-zinc-500">
                {t('Quick aggregate snapshot', 'Snapshot aggregato')}
              </div>
            </div>
          </div>
        </Button>

        <Button
          variant="outline"
          onClick={() => downloadPdf('combined')}
          disabled={!!busyKind}
          className="border-cyan-500/40 text-cyan-100 bg-cyan-950/30 hover:bg-cyan-900/40 justify-start h-auto py-3"
          data-testid="combined-report-export-btn"
        >
          <div className="flex items-center gap-3 w-full">
            {busyKind === 'combined' ? (
              <Loader2 className="w-5 h-5 animate-spin text-cyan-300 shrink-0" />
            ) : (
              <Layers className="w-5 h-5 text-cyan-300 shrink-0" />
            )}
            <div className="text-left">
              <div className="text-sm font-medium">
                {t('Combined Athlete Report', 'Report Combinato Atleta')}
              </div>
              <div className="text-xs text-cyan-200/80">
                {t(
                  'Flagship premium · all modules',
                  'Premium flagship · tutti i moduli',
                )}
              </div>
            </div>
          </div>
        </Button>
      </div>
    </Card>
  );
};

export default AthleteReports;
